<template>

    <div class="bg-white rounded-lg shadow mt-2" @click.stop="" v-show="totalItemsCount > 0">

        <div class="bg-gray-100 rounded border-t border-gray-100 cursor-pointer hover:bg-gray-200 transition">

            <div class="p-3 flex justify-between items-center text-sm gap-x-1" @click.stop="toggleList()">
                <button v-if="progressCount >= totalItemsCount || !isTaskActive" @click.stop="confirmDismiss"
                    class="text-gray-400 hover:text-gray-700 p-1 rounded-full transition-colors duration-150 focus:outline-none"
                    aria-label="Hide last run progress" title="Hide last run">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <div class="text-gray-600 flex-grow" @click.stop="toggleList()">
                    <span v-if="progressCount < totalItemsCount && isTaskActive">Progress</span>
                    <span v-else>Last Run</span>
                    <span v-if="props.taskDisplayName"> (<strong class="italic">{{ props.taskDisplayName
                    }}</strong>)</span>:
                    <span class="font-semibold text-gray-800">{{ progressCount
                    }} / {{ totalItemsCount
                        }}</span>
                </div>
                <div class="flex flex-col justify-content-center items-center">
                    <div class="mr-2" v-if="totalSkippedItems > 0">
                        <span
                            class="text-xs font-semibold text-white bg-[#f97316] px-2 py-0.5 rounded-full whitespace-nowrap overflow-hidden text-ellipsis max-w-full inline-block">
                            {{ totalSkippedItems }} item(s) skipped
                        </span>
                    </div>
                    <div class="text-blue-600 hover:text-blue-800 font-medium flex items-center" v-if="!isEnabled"
                        @click.stop="toggleList()">
                        View Items
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                    <div class="text-blue-600 hover:text-blue-800 font-medium flex items-center" v-else
                        @click.stop="toggleList()">
                        Hide Items
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                        </svg>
                    </div>
                </div>

            </div>

            <div class="w-full bg-gray-200 h-1.5" v-if="progressCount < totalItemsCount && isTaskActive">

                <div class="bg-blue-600 h-1.5" :style="`width: ${progressPercentage}%`"></div>
            </div>
        </div>

        <div class="bg-white p-4 border-t border-gray-100 result-item-list max-h-80 overflow-y-auto" v-show="isEnabled">
            <div v-if="items.length">
                <div v-for="(item, index) in items" :key="index"
                    class="flex flex-col items-start justify-between border border-blue-500 rounded-lg mt-1 hover:cursor-pointer"
                    @click="
                        sendMessage({
                            message: 'openLink',
                            link: generateLinkForListing(item)
                        })
                        ">
                    <div class="flex gap-x-3 w-full p-1">
                        <img v-if="!item.is_user_listing" style="width: 50px; height: 50px"
                            :src="'https://di2ponv0v5otw.cloudfront.net/' + item.thumbnail" alt="product Image" />
                        <img v-else style="width: 50px; height: 50px" :src="item.thumbnail" alt="profile Image" />
                        <div class="flex-grow flex flex-col gap-y-1">
                            <h6>{{ item.is_user_listing ? "@" : '' }}{{ item.title }}</h6>
                            <p class="italic text-sm text-gray-500">{{ new
                                Date(item.date).toLocaleDateString() }}
                            </p>
                            <Tag v-if="item.skipped" icon="pi pi-exclamation-triangle" class="w-20" severity="warning"
                                value="Skipped">
                            </Tag>
                        </div>
                        <div class="flex justify-end">
                            <i v-if="copiedId !== item.id"
                                class="pi pi-copy text-blue-500 mr-1 text-[20px] cursor-pointer"
                                @click.stop="handleCopy(item)"></i>

                            <i v-else class="pi pi-check text-green-500 mr-1 text-[20px] transition duration-300"></i>
                        </div>
                    </div>
                    <div v-if="item.skipped && item.skipped_reason"
                        class="w-full mt-2 px-2 py-2 border-t border-yellow-200">
                        <p class="text-sm text-yellow-800">
                            <strong>Poshmark Error:</strong> <span class="italic">{{ item.skipped_reason }}</span>
                        </p>
                    </div>
                </div>
            </div>
            <div v-else>
                <h3>Processed items will appear here.</h3>
            </div>
        </div>


    </div>

</template>

<script setup lang="ts">
import { copyToClipboard, generateLinkForListing, sendMessage } from '@/utils';
import { computed, ref } from 'vue';
import type TaskResultItem from "@/types/TaskResultItem"
import Tag from 'primevue/tag';

const props = defineProps<{
    items: TaskResultItem[]
    progressCount: number,
    totalItemsCount: number,
    isTaskActive: boolean,
    taskDisplayName?: string
}>()

const emit = defineEmits(['hide-results'])

const copiedId = ref<string | null>(null);
const isEnabled = ref(false)
const progressPercentage = computed((): number => {
    if (props.totalItemsCount <= 0) return 0
    if (props.progressCount >= props.totalItemsCount) return 100
    return Math.floor((props.progressCount / props.totalItemsCount) * 100)
})

const totalSkippedItems = computed((): number => {
    return props.items.filter(item => item.skipped).length;
})


const toggleList = () => {
    isEnabled.value = !isEnabled.value
}

const handleCopy = async (item: TaskResultItem): Promise<void> => {

    const text = generateLinkForListing(item)
    await copyToClipboard(text);

    copiedId.value = item.id;

    setTimeout(() => {
        copiedId.value = null;
    }, 500);
}

const confirmDismiss = () => {
    emit("hide-results")
}


</script>

<style scoped>
/*Styling scroll bar */

.result-item-list::-webkit-scrollbar {
    /* width of the entire scrollbar */
    width: 12px;
}

.result-item-list::-webkit-scrollbar-thumb {
    /* color of the scroll thumb */
    background-color: rgb(0, 112, 183, 0.6);
    /* roundness of the scroll thumb */
    border-radius: 20px;
    /* creates padding around scroll thumb */
    border: 1px solid white;
}

.result-item-list::-webkit-scrollbar-track {
    /* creates padding around scroll thumb */
    border: 1px solid white;
    background-color: white;
}
</style>